import React from 'react';

import { useAppContext } from '../../../../app/provider/useAppContext';
import { useEventContext } from '../../../../app/provider/useEventContext';
import { useToast } from '../../../../app/provider/useToast';
import { appConfig } from '../../../../config/app-config';
import Loader from '../../../../shared/components/Loader';
import { STORAGE_KEYS } from '../../../../shared/utils/storage';
import { ShareBasketModal } from '../../../shared-basket';
import { useCartPage } from '../../hooks/useCartPage';
import { useCartPageMetadata } from '../../hooks/useCartPageMetadataHook';
import { useCartPageShare } from '../../hooks/useCartPageShare';
import { CartGroupView, AddOnConfigData } from '../../types/cartTypes';
import { resolveCartCurrency } from '../../utils/cartFormatting';


import CartEmptyState from './cartEmptyState';
import CartHeader from './cartHeader';
import CartItemsList from './cartItemsList';
import CartSummary from './cartSummary';


const config = { locale: appConfig.defaults.locale };

const CartPage: React.FC = () => {
  const { showSuccess } = useToast();
  const [context] = useAppContext();
  const { customerGroups } = useEventContext();

  const {
    cart,
    fetched,
    setIsUpdating,
    getCurrentCart,
    handleLogin,
    incrementQuantity,
    decrementQuantity,
    checkout,
    storeKey,
    eventKey,
    scopedKey,
    showError,
  } = useCartPage({ config });

  const { productMetadata, availableAddOns, metadataLoaded } = useCartPageMetadata(cart, config);
  const {
    isShareModalOpen,
    openShareModal,
    closeShareModal,
    handleShareSubmit,
    handleShareError,
  } = useCartPageShare({ cart, showError }); 

  const hasLocalCart = !!(sessionStorage.getItem(scopedKey) || localStorage.getItem(scopedKey));
  
  if ((!cart && !fetched && hasLocalCart) || (cart?.lineItems?.length && !metadataLoaded)) {
    return (
      <div className="w-full flex items-center justify-center p-6 bg-transparent min-h-0 overflow-x-hidden" style={{ minHeight: 'calc(100vh - 160px)' }}>
        <Loader text="Loading basket..." fullScreen={true} />
      </div>
    );
  }

  const pingToken = localStorage.getItem(STORAGE_KEYS.PING_ACCESS_TOKEN);
  const isLoggedIn = !!(context?.loggedIn || pingToken);

  if (!cart || !cart.lineItems || cart.lineItems.length === 0) {
    return (
      <CartEmptyState 
        storeKey={storeKey} 
        eventKey={eventKey} 
        isLoggedIn={isLoggedIn} 
        onLogin={handleLogin} 
      />
    );
  }

  const currency = resolveCartCurrency(cart, context?.currency as string);

  return (
    <div className="w-full flex justify-center p-6 bg-transparent min-h-0 overflow-x-hidden">
      <div className="w-full max-w-[1240px] grid grid-cols-1 md:grid-cols-[1fr_350px] gap-8 items-start overflow-x-hidden overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div>
          <CartHeader 
            storeKey={storeKey} 
            eventKey={eventKey} 
            linkText="Add more passes" 
          />
          
          <CartItemsList
            groupedItems={cart.groupedItems ?? []}
            productMetadata={productMetadata}
            availableAddOns={availableAddOns}
            customerGroups={customerGroups}
            config={config}
            currency={currency}
            onIncrement={incrementQuantity}
            onDecrement={decrementQuantity}
            onConfigureAddOns={async (group: CartGroupView, data: AddOnConfigData) => {
              try {
                const { configureAddOns } = await import('../../services/configureAddOns');
                const lineItemAvailableAddOns = availableAddOns.products.filter((addon: { id: string }) => 
                  (availableAddOns.productAddOnsMap[group.mainItem?.productId ?? ''] ?? []).includes(addon.id)
                );
                
                setIsUpdating(true);
                const selectedProducts = lineItemAvailableAddOns.filter((p: { id: string }) =>
                  data.selectedAddOnIds.includes(p.id)
                );
                await configureAddOns(group, selectedProducts, lineItemAvailableAddOns, data.selectedOption ?? null, cart);
                await getCurrentCart(true);
                showSuccess('Your pass has been updated');
              } finally {
                setIsUpdating(false);
              }
            }}
            locale={(context?.locale as string) || 'en'}
            isLoggedIn={isLoggedIn}
            onLogin={handleLogin}
          />
        </div>

        <CartSummary
          cart={cart}
          currency={currency}
          productMetadata={productMetadata}
          onCheckout={checkout}
          onShare={openShareModal}
          isShareModalOpen={isShareModalOpen}
          locale={(context?.locale as string) || 'en'}
        />
      </div>

      <ShareBasketModal
        isOpen={isShareModalOpen}
        onClose={closeShareModal}
        basketData={null}
        userDetails={{
          firstName: context?.userInfo?.name ?? '',
          email: context?.userInfo?.email ?? '',
        }}
        onShare={handleShareSubmit as Parameters<typeof ShareBasketModal>[0]['onShare']}
        onError={handleShareError}
      />
    </div>
  );
};

export default CartPage;
